import type { NostrEvent } from '@nostrify/nostrify';
import {
  finalizeEvent,
  generateSecretKey,
  getEventHash,
  nip44,
} from 'nostr-tools';
import type { ArticleDraft } from '@/lib/articleDrafts';

/** Returns a timestamp up to ~48 hours in the past, jittered to thwart time analysis. */
function randomPastTimestamp(): number {
  return Math.floor(Date.now() / 1000 - Math.random() * 48 * 3600);
}
/** Addressable kind for a private article-draft rumor (see `NIP.md`). */
export const DRAFT_RUMOR_KIND = 31719;

const NIP59_GIFT_WRAP_KIND = 1059;
const NIP59_SEAL_KIND = 13;
const DRAFT_SYNC_TAG = 'wikistar-article-draft';

interface NostrSignerLike {
  signEvent(event: {
    kind: number;
    content: string;
    tags: string[][];
    created_at: number;
  }): Promise<NostrEvent>;
  nip44?: {
    encrypt(recipientPubkey: string, plaintext: string): Promise<string>;
    decrypt(senderPubkey: string, ciphertext: string): Promise<string>;
  };
}

function draftToRumorContent(draft: ArticleDraft): string {
  return JSON.stringify({
    title: draft.title,
    summary: draft.summary,
    content: draft.content,
    slug: draft.slug,
    updatedAt: draft.updatedAt,
  });
}

function rumorToDraft(id: string, event: NostrEvent, content: string): ArticleDraft | undefined {
  try {
    const parsed = JSON.parse(content) as Partial<ArticleDraft>;
    return {
      id,
      pubkey: event.pubkey,
      title: typeof parsed.title === 'string' ? parsed.title : '',
      summary: typeof parsed.summary === 'string' ? parsed.summary : '',
      content: typeof parsed.content === 'string' ? parsed.content : '',
      slug: typeof parsed.slug === 'string' ? parsed.slug : '',
      createdAt: event.created_at,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : event.created_at,
    };
  } catch {
    return undefined;
  }
}

/**
 * Wraps a draft in a NIP-59 gift wrap addressed to the author's own
 * pubkey. Every device the user is logged into can then unwrap it.
 */
export async function giftWrapDraft(
  draft: ArticleDraft,
  signer: NostrSignerLike,
  userPubkey: string,
): Promise<NostrEvent> {
  if (!signer.nip44) {
    throw new Error('Your signer does not support NIP-44 encryption. Please upgrade it to sync drafts.');
  }

  const rumor: Omit<NostrEvent, 'sig'> = {
    id: '',
    pubkey: userPubkey,
    // Rumor timestamp must be meaningful — newest-revision-wins across
    // devices relies on the rumor event's `created_at`, and a random
    // past-timestamp jitter on every layer (bug) would turn sync-down's
    // newest-wins into a random coin flip that discards remote drafts.
    // Jitter only applies to the seal/wrap timestamps below, where
    // it's designed to thwart time analysis per NIP-59.
    created_at: Math.floor(Date.now() / 1000),
    kind: DRAFT_RUMOR_KIND,
    content: draftToRumorContent(draft),
    tags: [['d', draft.id], ['t', DRAFT_SYNC_TAG], ['title', draft.title || 'Untitled draft']],
  };
  rumor.id = getEventHash(rumor as NostrEvent);

  // Seal: encrypt rumor to self, signed by the author.
  const sealedContent = await signer.nip44.encrypt(userPubkey, JSON.stringify(rumor));
  const seal = await signer.signEvent({
    kind: NIP59_SEAL_KIND,
    content: sealedContent,
    tags: [],
    created_at: randomPastTimestamp(),
  });

  // Wrap: encrypt the seal to self again with an ephemeral random key.
  const randomKey = generateSecretKey();
  const wrappedContent = nip44.v2.encrypt(
    JSON.stringify(seal),
    nip44.v2.utils.getConversationKey(randomKey, userPubkey),
  );

  return finalizeEvent(
    {
      kind: NIP59_GIFT_WRAP_KIND,
      content: wrappedContent,
      tags: [['p', userPubkey]],
      created_at: randomPastTimestamp(),
    },
    randomKey,
  );
}

/**
 * Unwraps a NIP-59 gift wrap back into a draft. Each layer uses the
 * *sender of that layer* as the decryption peer, matching NIP-59.
 */
export async function unwrapDraftGiftWrap(
  wrap: NostrEvent,
  signer: NostrSignerLike,
): Promise<ArticleDraft | undefined> {
  if (!signer.nip44) return undefined;

  // Unwrap the gift wrap: peer is the random ephemeral key that signed it.
  const sealJson = await signer.nip44.decrypt(wrap.pubkey, wrap.content);
  const seal = JSON.parse(sealJson) as NostrEvent;
  if (seal.kind !== NIP59_SEAL_KIND) return undefined;

  // Unseal: peer is the author (the one who signed the seal).
  const rumorJson = await signer.nip44.decrypt(seal.pubkey, seal.content);
  const rumor = JSON.parse(rumorJson) as NostrEvent;
  if (rumor.kind !== DRAFT_RUMOR_KIND) return undefined;

  const dTag = rumor.tags.find(([name]) => name === 'd')?.[1];
  if (!dTag) return undefined;
  // Only accept events explicitly tagged as draft syncs — kind 1059 is
  // shared with ordinary NIP-17 DMs, which must never be interpreted as
  // drafts.
  const syncTag = rumor.tags.find(([name]) => name === 't')?.[1];
  if (syncTag !== DRAFT_SYNC_TAG) return undefined;

  return rumorToDraft(dTag, rumor, rumor.content);
}

export { DRAFT_SYNC_TAG };
